/**
 * Exception Detector — identifies clinically significant changes in patient data.
 *
 * Designed for the "three-second rule": clinicians should spot what needs
 * attention within three seconds of looking at the report. Returns a sorted
 * list of exceptions: critical → attention → improved → stable.
 */

import type { DailyMetrics, PHQ4Entry } from '../types';

// ── Types ────────────────────────────────────────────────────────────────────

export type ExceptionCategory = 'critical' | 'attention' | 'improved' | 'stable';

export interface Exception {
  category: ExceptionCategory;
  pillar: string;
  metric: string;
  summary: string;
  currentValue: number | null;
  previousValue: number | null;
  changeDirection: 'increased' | 'decreased' | 'new' | null;
  changePercent: number | null;
  clinicalNote: string;
}

interface PillarScore {
  score: number | null;
  label?: string;
}

interface LabResult {
  marker: string;
  value: number;
  ref_low: number | null;
  ref_high: number | null;
  date: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function mean(vals: number[]): number {
  if (vals.length === 0) return 0;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function pctChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / Math.abs(previous)) * 100);
}

// ── Exception Detection ─────────────────────────────────────────────────────

/**
 * Analyze patient data and identify clinically significant exceptions.
 * Returns exceptions sorted: critical first, then attention, then improved.
 */
export function detectExceptions(params: {
  currentPillarScores: Record<string, PillarScore>;
  previousPillarScores?: Record<string, PillarScore>;
  dailySummaries: DailyMetrics[];
  labs: LabResult[];
  previousLabs?: LabResult[];
  phq4History: PHQ4Entry[];
  nonResponseStatus?: string | null;
  nonResponseSummary?: string | null;
  weeksSinceTreatment?: number | null;
}): Exception[] {
  const exceptions: Exception[] = [];
  const {
    currentPillarScores, previousPillarScores,
    dailySummaries, labs, previousLabs,
    phq4History, nonResponseStatus, nonResponseSummary, weeksSinceTreatment,
  } = params;

  // ── PHQ-4 checks ──────────────────────────────────────────────────────────

  if (phq4History.length >= 2) {
    const recent = phq4History[0];
    const prior = phq4History[1];
    const recentScore = recent.q1 + recent.q2 + recent.q3 + recent.q4;
    const priorScore = prior.q1 + prior.q2 + prior.q3 + prior.q4;
    const change = recentScore - priorScore;

    // PHQ-4 spike: increased ≥ 3 points or score ≥ 9
    if (change >= 3 || recentScore >= 9) {
      exceptions.push({
        category: 'critical',
        pillar: 'Mental Health',
        metric: 'PHQ-4 Total',
        summary: `PHQ-4 ${change >= 0 ? 'increased' : 'changed'} ${Math.abs(change)} points (${priorScore}→${recentScore})`,
        currentValue: recentScore, previousValue: priorScore,
        changeDirection: change > 0 ? 'increased' : 'decreased',
        changePercent: pctChange(recentScore, priorScore),
        clinicalNote: recentScore >= 9
          ? 'PHQ-4 >= 9 suggests severe anxiety/depression symptoms. Consider clinical assessment.'
          : 'Significant worsening in self-reported mood and anxiety symptoms.',
      });
    }

    // PHQ-4 sustained elevation: ≥ 6 for 3+ consecutive weeks
    if (phq4History.length >= 3) {
      const last3Scores = phq4History.slice(0, 3).map(e => e.q1 + e.q2 + e.q3 + e.q4);
      if (last3Scores.every(s => s >= 6)) {
        exceptions.push({
          category: 'critical',
          pillar: 'Mental Health',
          metric: 'PHQ-4 Sustained',
          summary: `PHQ-4 >= 6 for 3+ consecutive weeks (${last3Scores.join(', ')})`,
          currentValue: last3Scores[0], previousValue: null,
          changeDirection: null, changePercent: null,
          clinicalNote: 'Sustained moderate-to-severe symptoms for 3+ weeks.',
        });
      }
    }

    // PHQ-4 improvement
    if (change <= -3) {
      exceptions.push({
        category: 'improved',
        pillar: 'Mental Health',
        metric: 'PHQ-4 Total',
        summary: `PHQ-4 improved ${Math.abs(change)} points (${priorScore}→${recentScore})`,
        currentValue: recentScore, previousValue: priorScore,
        changeDirection: 'decreased', changePercent: pctChange(recentScore, priorScore),
        clinicalNote: 'Meaningful improvement in self-reported symptoms.',
      });
    }
  }

  // ── Check-in non-completion ───────────────────────────────────────────────

  if (phq4History.length > 0) {
    const lastDate = new Date(phq4History[0].created_at);
    const daysSince = Math.floor((Date.now() - lastDate.getTime()) / 86400000);
    if (daysSince >= 14) {
      exceptions.push({
        category: 'attention',
        pillar: 'Engagement',
        metric: 'Check-In Completion',
        summary: `No self-report check-in for ${Math.floor(daysSince / 7)} weeks`,
        currentValue: daysSince, previousValue: null,
        changeDirection: null, changePercent: null,
        clinicalNote: 'Non-response may itself be a signal. Consider discussing.',
      });
    }
  } else {
    exceptions.push({
      category: 'attention',
      pillar: 'Engagement',
      metric: 'Check-In Completion',
      summary: 'Patient has never completed a check-in',
      currentValue: null, previousValue: null,
      changeDirection: null, changePercent: null,
      clinicalNote: 'No baseline self-report data available.',
    });
  }

  // ── Circadian phase shift ─────────────────────────────────────────────────

  if (dailySummaries.length >= 5) {
    const recent5 = dailySummaries.slice(-5);
    const withBedtime = recent5.filter(d => d.bedtime_start && d.total_sleep_mins && d.total_sleep_mins > 0);
    // Crude Z-score estimate: check if bedtime_start variance is high
    // (Detailed Z-score is in pillar_scores; here we flag sustained shifts)
    if (withBedtime.length >= 3) {
      const bedtimeHours = withBedtime.map(d => {
        const bt = new Date(d.bedtime_start!);
        let h = bt.getHours() + bt.getMinutes() / 60;
        if (h < 12) h += 24; // normalize past-midnight bedtimes
        return h;
      });
      const avgBedtime = mean(bedtimeHours);
      const rawDrift = bedtimeHours[bedtimeHours.length - 1] - avgBedtime;
      const drift = Math.abs(rawDrift);
      if (drift > 1.5) {
        exceptions.push({
          category: 'attention',
          pillar: 'Sleep',
          metric: 'Circadian Phase',
          summary: `Sleep timing shifted ${rawDrift > 0 ? 'later' : 'earlier'} by ${drift.toFixed(1)} hours over 5 days`,
          currentValue: Math.round(drift * 10) / 10, previousValue: null,
          changeDirection: 'increased', changePercent: null,
          clinicalNote: 'Significant sleep timing shift. Associated with mood episode risk.',
        });
      }
    }
  }

  // ── HRV decline ───────────────────────────────────────────────────────────

  if (dailySummaries.length >= 14) {
    const hrvValues = dailySummaries.filter(d => d.hrv != null && d.hrv > 0).map(d => d.hrv as number);
    if (hrvValues.length >= 10) {
      const baseline30 = mean(hrvValues);
      const recent7 = mean(hrvValues.slice(-7));
      const decline = pctChange(recent7, baseline30);
      if (decline <= -20) {
        exceptions.push({
          category: 'attention',
          pillar: 'Stress',
          metric: 'HRV',
          summary: `7-day HRV average dropped ${Math.abs(decline)}% from 30-day baseline`,
          currentValue: Math.round(recent7), previousValue: Math.round(baseline30),
          changeDirection: 'decreased', changePercent: decline,
          clinicalNote: 'Significant HRV decline. May indicate increased stress or autonomic dysregulation.',
        });
      }
    }
  }

  // ── Sleep variability spike ───────────────────────────────────────────────

  if (dailySummaries.length >= 14) {
    const effValues = dailySummaries.filter(d => d.sleep_efficiency != null).map(d => d.sleep_efficiency as number);
    if (effValues.length >= 7) {
      const baselineVals = effValues.slice(0, -7);
      const recentVals = effValues.slice(-7);
      const baselineMean = mean(baselineVals);
      const recentMean = mean(recentVals);
      const baselineSD = baselineVals.length > 0
        ? Math.sqrt(baselineVals.reduce((s, v) => s + (v - baselineMean) ** 2, 0) / baselineVals.length)
        : 0;
      const recentSD = Math.sqrt(recentVals.reduce((s, v) => s + (v - recentMean) ** 2, 0) / recentVals.length);
      if (baselineSD > 0 && recentSD / baselineSD > 1.5) {
        exceptions.push({
          category: 'attention',
          pillar: 'Sleep',
          metric: 'Sleep Variability',
          summary: `Sleep inconsistency increased ${Math.round((recentSD / baselineSD - 1) * 100)}% from baseline`,
          currentValue: Math.round(recentSD * 10) / 10, previousValue: Math.round(baselineSD * 10) / 10,
          changeDirection: 'increased', changePercent: Math.round((recentSD / baselineSD - 1) * 100),
          clinicalNote: 'Increased sleep inconsistency. More predictive of depression than sleep duration.',
        });
      }
    }
  }

  // ── Pillar score changes ──────────────────────────────────────────────────

  if (previousPillarScores) {
    const pillars = ['sleep', 'movement', 'nutrition', 'stress', 'social', 'substance'];
    const pillarNames: Record<string, string> = {
      sleep: 'Sleep', movement: 'Movement', nutrition: 'Nutrition',
      stress: 'Stress', social: 'Social', substance: 'Substances',
    };
    for (const key of pillars) {
      const current = currentPillarScores[key]?.score;
      const previous = previousPillarScores[key]?.score;
      if (current != null && previous != null) {
        const change = current - previous;
        if (change <= -15) {
          exceptions.push({
            category: 'attention',
            pillar: pillarNames[key],
            metric: `${pillarNames[key]} Score`,
            summary: `${pillarNames[key]} score declined ${Math.abs(change)} points (${previous}→${current})`,
            currentValue: current, previousValue: previous,
            changeDirection: 'decreased', changePercent: pctChange(current, previous),
            clinicalNote: `Significant decline in ${pillarNames[key].toLowerCase()} pillar.`,
          });
        } else if (change >= 15) {
          exceptions.push({
            category: 'improved',
            pillar: pillarNames[key],
            metric: `${pillarNames[key]} Score`,
            summary: `${pillarNames[key]} score improved ${change} points (${previous}→${current})`,
            currentValue: current, previousValue: previous,
            changeDirection: 'increased', changePercent: pctChange(current, previous),
            clinicalNote: `Meaningful improvement in ${pillarNames[key].toLowerCase()} pillar.`,
          });
        }
      }
    }
  }

  // ── Lab changes ───────────────────────────────────────────────────────────

  if (previousLabs && previousLabs.length > 0) {
    for (const currentLab of labs) {
      const prevLab = previousLabs.find(l => l.marker === currentLab.marker);
      if (!prevLab) continue;

      const wasInRange = prevLab.ref_low != null && prevLab.ref_high != null &&
        prevLab.value >= prevLab.ref_low && prevLab.value <= prevLab.ref_high;
      const isInRange = currentLab.ref_low != null && currentLab.ref_high != null &&
        currentLab.value >= currentLab.ref_low && currentLab.value <= currentLab.ref_high;

      if (wasInRange && !isInRange) {
        exceptions.push({
          category: 'attention',
          pillar: 'Labs',
          metric: currentLab.marker,
          summary: `${currentLab.marker} moved out of range (${prevLab.value}→${currentLab.value})`,
          currentValue: currentLab.value, previousValue: prevLab.value,
          changeDirection: currentLab.value > prevLab.value ? 'increased' : 'decreased',
          changePercent: pctChange(currentLab.value, prevLab.value),
          clinicalNote: `${currentLab.marker} moved from in-range to out-of-range.`,
        });
      } else if (!wasInRange && isInRange) {
        exceptions.push({
          category: 'improved',
          pillar: 'Labs',
          metric: currentLab.marker,
          summary: `${currentLab.marker} improved to in-range (${prevLab.value}→${currentLab.value})`,
          currentValue: currentLab.value, previousValue: prevLab.value,
          changeDirection: currentLab.value > prevLab.value ? 'increased' : 'decreased',
          changePercent: pctChange(currentLab.value, prevLab.value),
          clinicalNote: `${currentLab.marker} improved to reference range.`,
        });
      }
    }
  }

  // ── Missing data ──────────────────────────────────────────────────────────

  if (dailySummaries.length >= 14) {
    const recent14 = dailySummaries.slice(-14);
    const withSleep = recent14.filter(d => d.sleep_score != null || (d.total_sleep_mins != null && d.total_sleep_mins > 0));
    if (withSleep.length < 6) {
      exceptions.push({
        category: 'attention',
        pillar: 'Data',
        metric: 'Data Coverage',
        summary: `Only ${withSleep.length}/14 days of sleep data in last 2 weeks`,
        currentValue: withSleep.length, previousValue: 14,
        changeDirection: null, changePercent: null,
        clinicalNote: 'Sparse data may indicate disengagement. Consider discussing wearable adherence.',
      });
    }
  }

  // ── Non-response flag ─────────────────────────────────────────────────────

  if (nonResponseStatus === 'possible_non_response' && weeksSinceTreatment != null) {
    exceptions.push({
      category: 'critical',
      pillar: 'Treatment',
      metric: 'Non-Response',
      summary: nonResponseSummary || `Wearable trends suggest limited treatment response at week ${weeksSinceTreatment}`,
      currentValue: weeksSinceTreatment, previousValue: null,
      changeDirection: null, changePercent: null,
      clinicalNote: 'Consider treatment review, dose adjustment, or augmentation strategy.',
    });
  }

  // ── Sort: critical → attention → improved → stable ────────────────────────

  const priority: Record<ExceptionCategory, number> = { critical: 0, attention: 1, improved: 2, stable: 3 };
  exceptions.sort((a, b) => priority[a.category] - priority[b.category]);

  return exceptions;
}
